// Cart functionality for Shopify theme
class CartManager {
  constructor() {
    this.isUpdating = false;
    this.init();
  }

  init() {
    // Initialize cart functionality
    this.bindEvents();
    this.initializeCartCount();
  }

  bindEvents() {
    // Add to cart buttons
    document.addEventListener("click", (e) => {
      if (e.target.closest("[data-add-to-cart]")) {
        e.preventDefault();
        const button = e.target.closest("[data-add-to-cart]");
        this.addToCart(button);
      }
    });

    // Cart toggle
    window.toggleCart = () => this.toggleCart();

    // Update quantity buttons
    window.updateCartQuantity = (key, quantity) =>
      this.updateCartQuantity(key, quantity);

    // Remove item buttons
    window.removeCartItem = (key) => this.removeCartItem(key);

    // Proceed to checkout
    window.proceedToCheckout = () => this.proceedToCheckout();

    // Close cart when clicking outside
    document.addEventListener("click", (e) => {
      const cartContent = document.getElementById("cart-content");
      const cartButton = e.target.closest('[onclick="toggleCart()"]');

      if (
        cartContent &&
        !cartContent.classList.contains("hidden") &&
        !cartContent.contains(e.target) &&
        !cartButton
      ) {
        this.closeCart();
      }
    });

    // Close cart on escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.closeCart();
      }
    });
  }

  async addToCart(button) {
    if (this.isUpdating) return;

    const variantId = button.dataset.variantId;
    const quantity = parseInt(button.dataset.quantity) || 1;

    if (!variantId) {
      console.error("No variant ID found");
      // this.showToast("Product not available", "error");
      return;
    }

    // Check if button is disabled (out of stock)
    if (button.disabled) {
      // this.showToast("This item is out of stock", "error");
      return;
    }

    // Disable button and show loading state
    button.disabled = true;
    button.style.opacity = "0.5";

    try {
      this.isUpdating = true;

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add item to cart");
      }

      const data = await response.json();

      // Update cart UI
      await this.refreshCart();

      // Show success toast
      // this.showToast("Item added to cart!", "success");

      // Open cart if it's not already open
      this.openCart();
    } catch (error) {
      console.error("Error adding to cart:", error);
      // this.showToast(error.message || "Failed to add item to cart", "error");
    } finally {
      // Re-enable button
      button.disabled = false;
      button.style.opacity = "1";
      this.isUpdating = false;
    }
  }

  async updateCartQuantity(key, quantity) {
    if (this.isUpdating) return;

    // Show loading state
    const loadingSpinner = document.querySelector(`[data-loading="${key}"]`);
    const quantityElement = document.querySelector(`[data-quantity="${key}"]`);
    const cartItem = quantityElement?.closest(".flex.gap-6");

    if (loadingSpinner) loadingSpinner.classList.remove("hidden");
    if (cartItem) cartItem.classList.add("cart-item-updating");

    try {
      this.isUpdating = true;

      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update cart");
      }

      // Update cart UI
      await this.refreshCart();
    } catch (error) {
      console.error("Error updating cart:", error);
      // this.showToast("Failed to update cart", "error");
    } finally {
      // Hide loading state
      if (loadingSpinner) loadingSpinner.classList.add("hidden");
      if (cartItem) cartItem.classList.remove("cart-item-updating");
      this.isUpdating = false;
    }
  }

  async removeCartItem(key) {
    if (this.isUpdating) return;

    // Show loading state
    const deleteButton = document.querySelector(
      `[data-delete-button="${key}"]`,
    );
    const deleteLoading = document.querySelector(
      `[data-delete-loading="${key}"]`,
    );
    const trashIcon = document.querySelector(`[data-trash-icon="${key}"]`);
    const cartItem = deleteButton?.closest(".flex.gap-6");

    if (deleteLoading) deleteLoading.classList.remove("hidden");
    if (trashIcon) trashIcon.style.opacity = "0.3";
    if (cartItem) cartItem.classList.add("cart-item-updating");
    if (deleteButton) deleteButton.disabled = true;

    try {
      this.isUpdating = true;

      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: key,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to remove item");
      }

      // Update cart UI
      await this.refreshCart();
      // this.showToast("Item removed from cart", "success");
    } catch (error) {
      console.error("Error removing item:", error);
      // this.showToast("Failed to remove item", "error");
    } finally {
      // Hide loading state
      if (deleteLoading) deleteLoading.classList.add("hidden");
      if (trashIcon) trashIcon.style.opacity = "1";
      if (cartItem) cartItem.classList.remove("cart-item-updating");
      if (deleteButton) deleteButton.disabled = false;
      this.isUpdating = false;
    }
  }

  async refreshCart() {
    try {
      // Fetch updated cart data
      const response = await fetch("/cart.js");
      if (!response.ok) throw new Error("Failed to fetch cart");

      const cart = await response.json();

      // Update cart count badge
      this.updateCartCount(cart.item_count);

      // Update cart content
      await this.updateCartContent(cart);

      // Close cart if empty
      if (cart.item_count === 0) {
        this.closeCart();
      }
    } catch (error) {
      console.error("Error refreshing cart:", error);
    }
  }

  async initializeCartCount() {
    try {
      // Fetch current cart data to get the actual count
      const response = await fetch("/cart.js");
      if (!response.ok) throw new Error("Failed to fetch cart");

      const cart = await response.json();
      this.updateCartCount(cart.item_count);
    } catch (error) {
      console.error("Error initializing cart count:", error);
      // Fallback: try to get count from existing elements
      const counts = document.querySelectorAll("[data-cart-count]");
      if (counts.length > 0) {
        const count = parseInt(counts[0].textContent) || 0;
        this.updateCartCount(count);
      }
    }
  }

  updateCartCount(count) {
    const badges = document.querySelectorAll("[data-cart-count-badge]");
    const counts = document.querySelectorAll("[data-cart-count]");

    badges.forEach((badge) => {
      if (count > 0) {
        badge.textContent = count;
        badge.style.display = "flex";
      } else {
        badge.style.display = "none";
      }
    });

    counts.forEach((countElement) => {
      countElement.textContent = count;
    });
  }

  async updateCartContent(cart) {
    try {
      // Fetch the updated cart HTML using the cart endpoint with a custom view
      const response = await fetch("/cart?view=popup");
      if (!response.ok) throw new Error("Failed to fetch cart content");

      const html = await response.text();

      // Update cart content
      const cartContent = document.getElementById("cart-content");
      if (cartContent) {
        // Parse the response and extract just the cart content
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const newCartContent = doc.querySelector("#cart-content");

        if (newCartContent) {
          cartContent.innerHTML = newCartContent.innerHTML;
        } else {
          // If no cart-content found, try to get the body content
          const bodyContent = doc.querySelector("body");
          if (bodyContent) {
            cartContent.innerHTML = bodyContent.innerHTML;
          }
        }
      }
    } catch (error) {
      console.error("Error updating cart content:", error);
      // Fallback: reload the page
      window.location.reload();
    }
  }

  toggleCart() {
    const cartContent = document.getElementById("cart-content");
    if (cartContent) {
      cartContent.classList.toggle("hidden");
    }
  }

  openCart() {
    const cartContent = document.getElementById("cart-content");
    if (cartContent) {
      cartContent.classList.remove("hidden");
    }
  }

  closeCart() {
    const cartContent = document.getElementById("cart-content");
    if (cartContent) {
      cartContent.classList.add("hidden");
    }
  }

  proceedToCheckout() {
    window.location.href = "/checkout";
  }

  showToast(message, variant = "success") {
    // Use the existing toast system if available
    if (window.showToast) {
      window.showToast(message, variant);
    } else {
      console.log(`Toast: ${message}`);
    }
  }
}

// Initialize cart manager when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  window.cartManager = new CartManager();
});

// Also initialize if DOM is already loaded
if (document.readyState !== "loading") {
  window.cartManager = new CartManager();
}
